/**
 * Product-owned AuthGuard for the Better Auth provider (goal 047 + phone
 * verification).
 *
 * `_app.tsx` renders this instead of BetterAuth's <SignedIn>/<SignedOut>/
 * <RedirectToSignIn> when the Better Auth session is absent. The auth pages
 * are the live signed-out entry; every other route requires a session and
 * redirects to `/sign-in` otherwise (client-side transitions and sign-out —
 * `src/proxy.ts` already validates the session and redirects document
 * navigations server-side, so this guard is a second layer, not the only one).
 *
 * Phone verification (WhatsApp or SMS code through the auth Worker) is
 * required of accounts created on or after `PHONE_VERIFICATION_REQUIRED_SINCE`:
 * until their phone is verified they are redirected to `/verify-phone` on every
 * protected route. Older accounts, staff, impersonated sessions and devices
 * that deferred (only offered when we could not send a code) are never gated.
 */
import { useEffect, useRef, type ReactElement, type ReactNode } from 'react'

import Loading from '@/components/Loading'
import { useRouter } from '@/lib/navigation/router'

import { authClient } from '../lib/auth-client'
import { postSignInRedirect } from '../lib/post-sign-in-redirect'
import { currentNextPath, oauthResumePath } from '../lib/safe-next-path'

/** The product-owned auth pages (goal 046) — reachable signed-out. */
const PUBLIC_AUTH_PAGE_PATHS = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  // The server proxy still requires a session for document navigation, and
  // Better Auth requires it again when the user submits the consent decision.
  '/oauth/consent',
  // A contractor's W-9 / W-8 link: they have no account, the token in the path is the credential.
  '/tax-form',
]

/** The post-signup phone-verification screen — requires a session, but is exempt from ITS OWN redirect. */
const PHONE_VERIFY_PATH = '/verify-phone'

const PHONE_VERIFY_SKIP_KEY_PREFIX = 'nt-auth-phone-verify-skip:'

/**
 * Accounts created at or after this instant must verify a phone; older accounts are never redirected.
 * Set to the release day (owner, 2026-10-24), so accounts that paid before the gate shipped are not caught.
 */
export const PHONE_VERIFICATION_REQUIRED_SINCE = '2026-10-24T22:00:00.000Z'

/**
 * Whether `pathname` is signed-out-reachable: the auth pages, plus — in
 * development builds only — `/dev/*` demo pages (already public server-side in
 * `src/proxy.ts`'s public-route list). In production/test builds `/dev/*`
 * stays protected, unchanged.
 */
export function isPublicAuthPagePath(
  pathname: string,
  { allowDevPages = process.env.NODE_ENV === 'development' }: { allowDevPages?: boolean } = {},
): boolean {
  if (allowDevPages && (pathname === '/dev' || pathname.startsWith('/dev/'))) {
    return true
  }
  return PUBLIC_AUTH_PAGE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

/**
 * Whether `userId` previously dismissed the phone-verification prompt on this
 * device (localStorage — best-effort, never throws, defaults to "not
 * skipped" server-side/when storage is unavailable).
 */
export function hasSkippedPhoneVerification(userId: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(`${PHONE_VERIFY_SKIP_KEY_PREFIX}${userId}`) === '1'
  } catch {
    return false
  }
}

/** Mark phone verification as deferred for `userId` on this device. Never throws. */
export function skipPhoneVerification(userId: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(`${PHONE_VERIFY_SKIP_KEY_PREFIX}${userId}`, '1')
  } catch {
    // Best-effort — a storage failure must never block navigation.
  }
}

interface SessionUser {
  id: string
  role?: string | null
  phoneNumberVerified?: boolean | null
  createdAt?: string | Date | null
}

/** Whether this account must verify a phone before using the app. Pure: the device deferral is checked by the caller. */
function isPhoneVerificationRequired(user: SessionUser, impersonatedBy: string | null | undefined): boolean {
  if (user.phoneNumberVerified || user.role === 'admin' || impersonatedBy) return false
  if (!user.createdAt) return false
  const createdAt = new Date(user.createdAt).getTime()
  return Number.isFinite(createdAt) && createdAt >= Date.parse(PHONE_VERIFICATION_REQUIRED_SINCE)
}

export function BetterAuthGuard({ children }: { children: ReactElement | ReactNode }) {
  const router = useRouter()
  const { data: session, isPending } = authClient.useSession()
  const isPublic = isPublicAuthPagePath(router.pathname)
  const isSignInPage = router.pathname === '/sign-in'
  const isPhoneVerifyPage = router.pathname === PHONE_VERIFY_PATH
  const typedSession = session as { user?: SessionUser; session?: { impersonatedBy?: string | null } } | null
  const user = typedSession?.user ?? null
  const needsPhoneVerification =
    Boolean(user) &&
    isPhoneVerificationRequired(user!, typedSession?.session?.impersonatedBy) &&
    !hasSkippedPhoneVerification(user!.id)

  // Whether this tab has seen a session. The guard is mounted once at the root,
  // so this survives route changes and tells an ended session from a first visit.
  const hadSession = useRef(false)

  useEffect(() => {
    if (session) hadSession.current = true
    if (isSignInPage && !isPending && session) {
      // Signed in while /oauth2/authorize is waiting (passkey, or a session that
      // already existed): go back to it — a full navigation, since the authorize
      // endpoint is not an SPA route — ahead of the admin/dashboard destinations.
      const resume = oauthResumePath(window.location.search)
      if (resume) {
        postSignInRedirect.go(resume)
        return
      }
      // Destination hint only: /admin is still re-authorized by the proxy and
      // AdminLayout against ProductData before privileged content can render.
      // A customer sent to sign in again (e.g. by the security page) goes back
      // to the same-site `?next=`; everyone else to the dashboard.
      void router.replace(user?.role === 'admin' ? '/admin' : currentNextPath())
      return
    }
    if (!isPublic && !isPending && !session) {
      // Sign-in comes back to this page. Only a session that ended in this tab
      // (expiry, or a sign-out in another tab) gets the "You were signed out"
      // notice; a first visit on a deep link was never signed in.
      const next = encodeURIComponent(router.asPath)
      void router.replace(hadSession.current ? `/sign-in?reason=signed-out&next=${next}` : `/sign-in?next=${next}`)
      return
    }
    if (!isPublic && !isPhoneVerifyPage && !isPending && session && needsPhoneVerification) {
      void router.replace({ pathname: PHONE_VERIFY_PATH, query: { next: router.asPath } })
    }
  }, [isPublic, isSignInPage, isPhoneVerifyPage, isPending, session, user?.role, needsPhoneVerification, router])

  if (isPublic) {
    if (isSignInPage && session) return <Loading />
    return <>{children}</>
  }

  // Pending, or signed-out with the redirect in flight: never render (or
  // flash) protected content without a verified client session.
  if (isPending || !session) {
    return <Loading />
  }

  // Unverified phone, redirect to /verify-phone in flight: never flash the
  // destination route's content. The /verify-phone page itself is exempt.
  if (needsPhoneVerification && !isPhoneVerifyPage) {
    return <Loading />
  }

  return <>{children}</>
}
